import {
  Button,
  Form,
  Input,
  Modal,
  ModalProps,
  Select,
  Spin,
} from 'components/common'
import { OrderCancelReason } from 'lib/graphql/storeadmin'
import priceFormat from 'lib/str-formatter/price'
import { useState } from 'react'

export interface CancelConfirmationModalProps extends Omit<ModalProps, 'onOk'> {
  onOk: (values: OrderCancelForm) => Promise<void>
  onClose: (value: boolean) => void
  loading: boolean
  totalPrice: number
}

export type OrderCancelForm = {
  cancelReason: string
  cancellationFee: number
}

export const reasonSelectSet = (reason: string): string => {
  switch (reason) {
    case OrderCancelReason.Customer:
      return 'お客様依頼'
    case OrderCancelReason.Declined:
      return '支払い拒否'
    case OrderCancelReason.Fraud:
      return '不正な注文'
    case OrderCancelReason.Inventory:
      return '在庫不十分'
    default:
      return 'その他'
  }
}

export const CancelConfirmationModal = ({
  visible,
  onClose,
  onOk,
  loading,
  totalPrice,
}: CancelConfirmationModalProps) => {
  const [form] = Form.useForm<OrderCancelForm>()
  const [fee, setFee] = useState(totalPrice)

  /**
   * キャンセル理由の選択
   */
  const onSelect = (value: string) => {
    form.setFieldsValue({ cancelReason: value })
  }

  /**
   * キャンセル料金の入力（自由入力）
   */
  const handleChangeCancellationFee = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFee(Number(e.target.value))
    form.setFieldsValue({ cancellationFee: Number(e.target.value) })
  }

  /**
   * キャンセル料金の入力（セレクトボックス）
   * 小数点以下は切り捨て
   */
  const handleChangeCancellationFeeSelect = (value: string) => {
    let feeValue = 0
    switch (value) {
      case '1/2':
        feeValue = Math.floor(totalPrice / 2)
        break
      case '1/3':
        feeValue = Math.floor(totalPrice / 3)
        break
      case '2/3':
        feeValue = Math.floor((totalPrice / 3) * 2)
        break
      default:
        feeValue = totalPrice
        break
    }
    setFee(feeValue)
    form.setFieldsValue({ cancellationFee: feeValue })
  }

  /**
   * キャンセル処理
   */
  const onSubmit = async (values: OrderCancelForm) => {
    try {
      await onOk(values)
      onClose(false)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Modal
      title={'予約をキャンセルする'}
      visible={visible}
      onCancel={() => onClose(false)}
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(values))
          .catch((error) => console.error(error))
      }}
      footer={
        <div className="flex justify-end space-x-4">
          <Button onClick={() => onClose(false)} disabled={loading}>
            閉じる
          </Button>
          <Button
            type="primary"
            onClick={() => {
              form
                .validateFields()
                .then((values) => onSubmit(values))
                .catch((error) => console.error(error))
            }}
            disabled={loading}
          >
            実行
          </Button>
        </div>
      }
    >
      <Spin spinning={loading} tip="キャンセル処理を実行中...">
        <Form form={form} name="form_in_modal">
          <Form.Item
            name="cancelReason"
            label="キャンセル理由"
            rules={[
              { required: true, message: 'キャンセル理由を入力してください' },
            ]}
          >
            <Select
              onChange={onSelect}
              placeholder="キャンセル理由を選択してください"
            >
              {Object.entries(OrderCancelReason).map(([key, value]) => (
                <Select.Option key={key} value={key}>
                  {reasonSelectSet(value)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="cancellationFee"
            label={
              'キャンセル料金（請求合計金額：' + priceFormat(totalPrice) + '）'
            }
            rules={[
              { required: true, message: 'キャンセル料金を入力してください' },
            ]}
            initialValue={totalPrice}
          >
            <div className="grid grid-cols-3">
              <Input
                prefix={'￥'}
                min={0}
                type="number"
                onChange={handleChangeCancellationFee}
                defaultValue={totalPrice}
                value={fee}
                className="col-span-2"
              />
              <Select
                defaultValue="0"
                className="col-span-1"
                onChange={(value) => handleChangeCancellationFeeSelect(value)}
              >
                <Select.Option value="0">---</Select.Option>
                <Select.Option value="1/2">1/2</Select.Option>
                <Select.Option value="1/3">1/3</Select.Option>
                <Select.Option value="2/3">2/3</Select.Option>
              </Select>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
}
